import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, formatApiError } from "../api";

const TYPES = [
  ["UNIVERSITY", "Uniwersytet publiczny"],
  ["TECHNICAL_UNIVERSITY", "Uczelnia techniczna"],
  ["AGRICULTURAL_UNIVERSITY", "Uniwersytet przyrodniczy/rolniczy"],
  ["ECONOMICS_BUSINESS_UNIVERSITY", "Uczelnia ekonomiczna/biznesowa"],
  ["ACADEMY", "Akademia"],
  ["PRIVATE_UNIVERSITY", "Uczelnia niepubliczna"],
  ["ACCREDITED_HEI", "Inna akredytowana uczelnia"],
];

export default function EduApplyPage() {
  const [form, setForm] = useState({ institution_name: "", institution_type: "UNIVERSITY",
    city: "", email_domain: "", contact_name: "", contact_email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      let ref;
      try { ref = sessionStorage.getItem("pg_ref") || undefined; } catch { ref = undefined; }
      const r = await api.post("/edu/apply", { ...form, ...(ref ? { ref } : {}) });
      setDone(r.data);
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally { setBusy(false); }
  };

  if (done) return (
    <div className="mx-auto max-w-xl px-4 py-16 text-center" data-testid="edu-apply-success">
      <Helmet><title>Zgłoszenie przyjęte — PlanGmina EDU</title></Helmet>
      <h1 className="text-2xl font-bold text-slate-900">Zgłoszenie przyjęte</h1>
      <p className="mt-3 text-sm text-slate-600">{done.message_pl}</p>
      <p className="mt-2 text-xs text-slate-500">Numer zgłoszenia: {done.id}</p>
      <Link to="/edu" className="mt-6 inline-block rounded-lg border border-slate-300 px-4 py-2 text-sm" data-testid="edu-apply-back">Wróć do PlanGmina EDU</Link>
    </div>);

  return (
    <div className="mx-auto max-w-xl px-4 py-10" data-testid="edu-apply-page">
      <Helmet><title>Zgłoś uczelnię — PlanGmina EDU</title></Helmet>
      <h1 className="text-2xl font-bold text-slate-900">Zgłoś uczelnię do PlanGmina EDU</h1>
      <p className="mt-2 text-sm text-slate-600">
        Zgłoszenie nie zobowiązuje do niczego. Po weryfikacji domeny uczelni aktywujemy dostęp
        i przekażemy administratorowi zaproszenie.
      </p>
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="edu-apply-form">
        <div>
          <label className="text-xs font-semibold uppercase text-slate-500">Nazwa uczelni</label>
          <input required value={form.institution_name} onChange={set("institution_name")}
            data-testid="edu-apply-name" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase text-slate-500">Typ uczelni</label>
          <select value={form.institution_type} onChange={set("institution_type")}
            data-testid="edu-apply-type" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
            {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="text-xs font-semibold uppercase text-slate-500">Miasto</label>
            <input required value={form.city} onChange={set("city")}
              data-testid="edu-apply-city" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-slate-500">Domena e-mail uczelni</label>
            <input required placeholder="np. uw.edu.pl" value={form.email_domain} onChange={set("email_domain")}
              data-testid="edu-apply-domain" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="text-xs font-semibold uppercase text-slate-500">Osoba kontaktowa</label>
            <input required value={form.contact_name} onChange={set("contact_name")}
              data-testid="edu-apply-contact" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-slate-500">E-mail służbowy</label>
            <input required type="email" value={form.contact_email} onChange={set("contact_email")}
              data-testid="edu-apply-email" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase text-slate-500">Wiadomość (opcjonalnie)</label>
          <textarea rows={3} value={form.message} onChange={set("message")}
            data-testid="edu-apply-message" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <p className="mt-1 text-xs text-slate-500" data-testid="edu-apply-message-hint">
            Nie podawaj danych osobowych studentów ani pracowników poza osobą kontaktową.
          </p>
        </div>
        <p className="text-xs text-slate-500" data-testid="edu-apply-legal">
          Wysyłając zgłoszenie akceptujesz przetwarzanie danych osoby kontaktowej zgodnie z{" "}
          <Link to="/edu/prywatnosc" className="underline" data-testid="edu-apply-privacy-link">informacją o przetwarzaniu danych</Link>.
          Obowiązujący {" "}
          <Link to="/edu/regulamin" className="underline" data-testid="edu-apply-terms-link">Regulamin PlanGmina EDU</Link>.
        </p>
        {err && <p className="text-sm text-red-600" data-testid="edu-apply-error">{err}</p>}
        <button type="submit" disabled={busy} data-testid="edu-apply-submit"
          className="w-full rounded-lg bg-red-700 px-4 py-3 text-sm font-semibold text-white hover:bg-red-800 disabled:opacity-50 transition-colors">
          {busy ? "Wysyłanie…" : "Wyślij zgłoszenie"}
        </button>
      </form>
    </div>
  );
}
